import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { useToast } from '../hooks/use-toast';
import { authAPI, dashboardAPI, chatbotAPI, handleAPIError } from '../services/api';
import { 
  X,
  Users, 
  UserPlus, 
  UserX, 
  Edit3, 
  Trash2, 
  Save, 
  Cancel,
  Bot,
  Clock,
  CheckCircle,
  AlertCircle,
  Rocket,
  BarChart3,
  Building,
  Mail,
  Phone,
  User,
  Loader2,
  ArrowRight,
  AlertTriangle
} from 'lucide-react';

const EmployeeManagementModal = ({ isOpen, onClose, onUpdate }) => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [employees, setEmployees] = useState([]);
  const [chatbots, setChatbots] = useState([]);
  const [assignments, setAssignments] = useState([]);
  
  // Modal states
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [showEditForm, setShowEditForm] = useState(false);
  const [showFireDialog, setShowFireDialog] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  
  // Form states
  const [createForm, setCreateForm] = useState({
    first_name: '',
    last_name: '',
    email: '',
    password: ''
  });
  const [editForm, setEditForm] = useState({
    first_name: '',
    last_name: '',
    email: ''
  });
  const [fireForm, setFireForm] = useState({
    reassignToEmployeeId: '',
    confirmationText: ''
  });
  
  // Loading states
  const [createLoading, setCreateLoading] = useState(false);
  const [editLoading, setEditLoading] = useState(false);
  const [fireLoading, setFireLoading] = useState(false);

  // Load all data when modal opens
  useEffect(() => {
    if (isOpen) {
      loadAllData();
    }
  }, [isOpen]);

  const loadAllData = async () => {
    setLoading(true);
    try {
      const [employeesRes, chatbotsRes] = await Promise.all([
        authAPI.getEmployees(),
        dashboardAPI.getMyChatbots()
      ]);
      
      // Ensure unique employees by filtering duplicates based on ID
      const employeesData = employeesRes.data || [];
      const uniqueEmployees = employeesData.filter((employee, index, self) => 
        index === self.findIndex(e => e.id === employee.id)
      );
      
      setEmployees(uniqueEmployees);
      setChatbots(chatbotsRes.data || []);
      
      console.log(`Loaded ${uniqueEmployees.length} unique employees (filtered from ${employeesData.length} total)`);
      
    } catch (error) {
      console.error('Error loading employee management data:', error);
      toast({
        title: "Error",
        description: "Failed to load employee data",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const refreshData = async () => {
    // Prevent multiple simultaneous refresh operations
    if (loading) {
      console.log('Data refresh already in progress, skipping duplicate request');
      return;
    }
    
    console.log('Refreshing employee management data...');
    await loadAllData();
    if (onUpdate) {
      console.log('Calling onUpdate callback...');
      onUpdate();
    }
  };

  // Get employee statistics
  const getEmployeeStats = (employee) => {
    const assignedChatbots = chatbots.filter(cb => cb.assigned_employee_id === employee.id);
    const stats = {
      total: assignedChatbots.length,
      in_progress: assignedChatbots.filter(cb => cb.status === 'in_progress').length,
      testing: assignedChatbots.filter(cb => cb.status === 'testing').length,
      deploy: assignedChatbots.filter(cb => cb.status === 'deploy').length,
      done: assignedChatbots.filter(cb => cb.status === 'done').length
    };
    return { stats, chatbots: assignedChatbots };
  };

  // Get pending chatbots (not assigned to anyone)
  const getPendingChatbots = () => {
    return chatbots.filter(cb => 
      !cb.assigned_employee_id && 
      ['pending', 'in_progress'].includes(cb.status)
    );
  };

  // Handle quick assign
  const handleQuickAssign = async (chatbotId, employeeId) => {
    try {
      await dashboardAPI.assignChatbot({
        chatbot_id: chatbotId,
        employee_id: employeeId,
        notes: "Assigned via Employee Management"
      });
      
      toast({
        title: "Assignment Successful",
        description: "Chatbot has been assigned successfully",
      });
      
      await refreshData();
    } catch (error) {
      console.error('Error assigning chatbot:', error);
      toast({
        title: "Assignment Failed", 
        description: handleAPIError(error, "Failed to assign chatbot"),
        variant: "destructive",
      });
    }
  };

  // Handle create employee
  const handleCreateEmployee = async (e) => {
    e.preventDefault();
    
    // Prevent multiple submissions
    if (createLoading) {
      console.log('Employee creation already in progress, ignoring duplicate submission');
      return;
    }
    
    if (!createForm.first_name || !createForm.last_name || !createForm.email || !createForm.password) {
      toast({
        title: "Validation Error",
        description: "Please fill in all required fields",
        variant: "destructive",
      });
      return;
    }

    // Check if email is already taken
    const existingEmployee = employees.find(emp => emp.email === createForm.email);
    if (existingEmployee) {
      toast({
        title: "Email Already Exists",
        description: "This email is already registered to an employee",
        variant: "destructive",
      });
      return;
    }

    setCreateLoading(true);
    try {
      const employeeData = {
        first_name: createForm.first_name,
        last_name: createForm.last_name,
        email: createForm.email,
        password: createForm.password,
        role: "employee"
      };
      
      console.log('Creating employee with data:', { ...employeeData, password: '[REDACTED]' });
      
      const response = await authAPI.createEmployee(employeeData);
      console.log('Employee creation response:', response);
      
      toast({
        title: "Employee Created",
        description: `${createForm.first_name} ${createForm.last_name} has been added as an employee`,
      });
      
      // Reset form and close
      setCreateForm({ first_name: '', last_name: '', email: '', password: '' });
      setShowCreateForm(false);
      
      // Refresh data to show the new employee
      await refreshData();
      
    } catch (error) {
      console.error('Error creating employee:', error);
      toast({
        title: "Creation Failed",
        description: handleAPIError(error, "Failed to create employee"),
        variant: "destructive",
      });
    } finally {
      setCreateLoading(false);
    }
  };

  // Handle edit employee
  const handleEditEmployee = async (e) => {
    e.preventDefault();
    
    // Prevent multiple submissions
    if (editLoading) {
      console.log('Update already in progress, ignoring duplicate submission');
      return;
    }
    
    if (!editForm.first_name || !editForm.last_name || !editForm.email) {
      toast({
        title: "Validation Error",
        description: "Please fill in all required fields",
        variant: "destructive",
      });
      return;
    }

    // Check if email is already taken by another employee
    const existingEmployee = employees.find(emp => 
      emp.email === editForm.email && emp.id !== selectedEmployee.id
    );
    
    if (existingEmployee) {
      toast({
        title: "Email Already Exists",
        description: "This email is already registered to another employee",
        variant: "destructive",
      });
      return;
    }

    setEditLoading(true);
    try {
      const updateData = {
        first_name: editForm.first_name,
        last_name: editForm.last_name,
        email: editForm.email
      };
      
      console.log(`Updating employee ${selectedEmployee.id} with data:`, updateData);
      
      const response = await authAPI.updateEmployee(selectedEmployee.id, updateData);
      console.log('Employee update response:', response);
      
      // Update local state immediately to prevent race conditions
      setEmployees(prevEmployees => 
        prevEmployees.map(emp => 
          emp.id === selectedEmployee.id 
            ? { ...emp, ...updateData }
            : emp
        )
      );
      
      toast({
        title: "Employee Updated",
        description: `${editForm.first_name} ${editForm.last_name} has been updated successfully`,
      });
      
      // Reset form and close
      setEditForm({ first_name: '', last_name: '', email: '' });
      setShowEditForm(false);
      setSelectedEmployee(null);
      
      // Refresh data to ensure we have the latest state from server
      await refreshData();
      
    } catch (error) {
      console.error('Error updating employee:', error);
      toast({
        title: "Update Failed",
        description: handleAPIError(error, "Failed to update employee"),
        variant: "destructive",
      });
    } finally {
      setEditLoading(false);
    }
  };

  // Handle fire employee
  const handleFireEmployee = async (e) => {
    e.preventDefault();
    
    if (fireForm.confirmationText.toUpperCase() !== 'FIRE') {
      toast({
        title: "Validation Error",
        description: "Please type 'FIRE' to confirm",
        variant: "destructive",
      });
      return;
    }

    const employeeData = getEmployeeStats(selectedEmployee);
    if (employeeData.chatbots.length > 0 && !fireForm.reassignToEmployeeId) {
      toast({
        title: "Reassignment Required",
        description: "Please select an employee to reassign the chatbots to",
        variant: "destructive",
      });
      return;
    }

    setFireLoading(true);
    try {
      // First reassign all chatbots if any
      if (employeeData.chatbots.length > 0) {
        for (const chatbot of employeeData.chatbots) {
          await chatbotAPI.reassignChatbot(chatbot.id, fireForm.reassignToEmployeeId);
        }
      }
      
      // Then delete the employee
      await authAPI.deleteEmployee(selectedEmployee.id);
      
      toast({
        title: "Employee Removed",
        description: `${selectedEmployee.first_name} ${selectedEmployee.last_name} has been removed and their chatbots reassigned`,
      });
      
      // Reset form and close
      setFireForm({ reassignToEmployeeId: '', confirmationText: '' });
      setShowFireDialog(false);
      setSelectedEmployee(null);
      await refreshData();
      
    } catch (error) {
      console.error('Error firing employee:', error);
      toast({
        title: "Removal Failed",
        description: handleAPIError(error, "Failed to remove employee"),
        variant: "destructive",
      });
    } finally {
      setFireLoading(false);
    }
  };

  // Start edit employee
  const startEditEmployee = (employee) => {
    setSelectedEmployee(employee);
    setEditForm({
      first_name: employee.first_name || '',
      last_name: employee.last_name || '',
      email: employee.email || ''
    });
    setShowEditForm(true);
  };

  // Start fire employee
  const startFireEmployee = (employee) => {
    setSelectedEmployee(employee);
    setFireForm({ reassignToEmployeeId: '', confirmationText: '' });
    setShowFireDialog(true);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
      <div className="bg-gray-900 border border-gray-700 rounded-lg w-full max-w-6xl max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-700">
          <div className="flex items-center space-x-3">
            <Users className="w-6 h-6 text-green-400" />
            <div>
              <h2 className="text-xl font-bold text-white">Employee Management</h2>
              <p className="text-gray-400 text-sm">Manage employees, assignments, and chatbot allocations</p>
            </div>
          </div>
          <Button
            onClick={onClose}
            variant="ghost"
            size="sm"
            className="text-gray-400 hover:text-white"
          >
            <X className="w-5 h-5" />
          </Button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-violet-400 mr-2" />
              <span className="text-gray-400">Loading employee data...</span>
            </div>
          ) : (
            <div className="space-y-6">
              {/* Quick Actions */}
              <div className="flex flex-wrap gap-3">
                <Button
                  onClick={() => setShowCreateForm(true)}
                  className="bg-green-600 hover:bg-green-700"
                >
                  <UserPlus className="w-4 h-4 mr-2" />
                  Add Employee
                </Button>
                <Button
                  onClick={refreshData}
                  variant="outline"
                  className="border-gray-600 text-gray-300 hover:bg-gray-700"
                >
                  <BarChart3 className="w-4 h-4 mr-2" />
                  Refresh Data
                </Button>
              </div>

              {/* Quick Assign Section */}
              {getPendingChatbots().length > 0 && (
                <Card className="bg-gray-800 border-gray-700">
                  <CardHeader>
                    <CardTitle className="text-white flex items-center">
                      <ArrowRight className="w-5 h-5 mr-2 text-orange-400" />
                      Quick Assign Pending Chatbots
                    </CardTitle>
                    <CardDescription>
                      Assign unassigned chatbots to employees
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {getPendingChatbots().map((chatbot) => (
                      <div key={chatbot.id} className="flex items-center justify-between p-3 bg-gray-700 rounded-lg">
                        <div className="flex items-center space-x-3">
                          <Building className="w-5 h-5 text-violet-400" />
                          <div>
                            <p className="text-white font-medium">{chatbot.company_name}</p>
                            <p className="text-gray-400 text-sm">{chatbot.company_website}</p>
                          </div>
                        </div>
                        <div className="flex items-center space-x-3">
                          <Badge className="bg-orange-600/20 text-orange-300 border-orange-500/30">
                            {chatbot.status.replace('_', ' ')}
                          </Badge>
                          <Select onValueChange={(employeeId) => handleQuickAssign(chatbot.id, employeeId)}>
                            <SelectTrigger className="w-48 bg-gray-600 border-gray-500 text-white">
                              <SelectValue placeholder="Assign to..." />
                            </SelectTrigger>
                            <SelectContent className="bg-gray-700 border-gray-600">
                              {employees.map((employee) => (
                                <SelectItem key={employee.id} value={employee.id} className="text-white hover:bg-gray-600">
                                  {employee.first_name} {employee.last_name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                    ))}
                  </CardContent>
                </Card>
              )}

              {/* Employees Overview */}
              <Card className="bg-gray-800 border-gray-700">
                <CardHeader>
                  <CardTitle className="text-white">Employees Overview</CardTitle>
                  <CardDescription>
                    {employees.length} employees • {chatbots.length} total chatbots
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {employees.length === 0 ? (
                    <div className="text-center py-8 text-gray-400">
                      <Users className="w-12 h-12 mx-auto mb-4 opacity-50" />
                      <p>No employees found. Create your first employee to get started.</p>
                    </div>
                  ) : (
                    employees.map((employee) => {
                      const { stats, chatbots: employeeChatbots } = getEmployeeStats(employee);
                      return (
                        <div key={employee.id} className="p-4 bg-gray-700 rounded-lg">
                          <div className="flex items-start justify-between mb-4">
                            <div className="flex items-center space-x-3">
                              <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-blue-600 rounded-lg flex items-center justify-center">
                                <User className="w-6 h-6 text-white" />
                              </div>
                              <div>
                                <h3 className="text-white font-semibold text-lg">
                                  {employee.first_name} {employee.last_name}
                                </h3>
                                <div className="flex items-center space-x-4 text-sm text-gray-400">
                                  <div className="flex items-center">
                                    <Mail className="w-4 h-4 mr-1" />
                                    {employee.email}
                                  </div>
                                  {employee.phone_number && (
                                    <div className="flex items-center">
                                      <Phone className="w-4 h-4 mr-1" />
                                      {employee.phone_number}
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                            
                            <div className="flex items-center space-x-2">
                              <Button
                                onClick={() => startEditEmployee(employee)}
                                size="sm"
                                variant="outline"
                                className="border-blue-500 text-blue-400 hover:bg-blue-500 hover:text-white"
                              >
                                <Edit3 className="w-4 h-4 mr-1" />
                                Edit
                              </Button>
                              <Button
                                onClick={() => startFireEmployee(employee)}
                                size="sm"
                                variant="outline"
                                className="border-red-500 text-red-400 hover:bg-red-500 hover:text-white"
                              >
                                <UserX className="w-4 h-4 mr-1" />
                                Remove
                              </Button>
                            </div>
                          </div>

                          {/* Statistics */}
                          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4">
                            <div className="text-center p-3 bg-gray-600 rounded-lg">
                              <Bot className="w-5 h-5 text-violet-400 mx-auto mb-1" />
                              <p className="text-xl font-bold text-white">{stats.total}</p>
                              <p className="text-xs text-gray-400">Total</p>
                            </div>
                            <div className="text-center p-3 bg-gray-600 rounded-lg">
                              <Clock className="w-5 h-5 text-yellow-400 mx-auto mb-1" />
                              <p className="text-xl font-bold text-white">{stats.in_progress}</p>
                              <p className="text-xs text-gray-400">In Progress</p>
                            </div>
                            <div className="text-center p-3 bg-gray-600 rounded-lg">
                              <AlertCircle className="w-5 h-5 text-blue-400 mx-auto mb-1" />
                              <p className="text-xl font-bold text-white">{stats.testing}</p>
                              <p className="text-xs text-gray-400">Testing</p>
                            </div>
                            <div className="text-center p-3 bg-gray-600 rounded-lg">
                              <Rocket className="w-5 h-5 text-green-400 mx-auto mb-1" />
                              <p className="text-xl font-bold text-white">{stats.deploy}</p>
                              <p className="text-xs text-gray-400">Deploy</p>
                            </div>
                            <div className="text-center p-3 bg-gray-600 rounded-lg">
                              <CheckCircle className="w-5 h-5 text-emerald-400 mx-auto mb-1" />
                              <p className="text-xl font-bold text-white">{stats.done}</p>
                              <p className="text-xs text-gray-400">Done</p>
                            </div>
                          </div>

                          {/* Assigned Chatbots List */}
                          {employeeChatbots.length > 0 && (
                            <div>
                              <p className="text-white font-medium mb-2">Assigned Chatbots:</p>
                              <div className="space-y-2">
                                {employeeChatbots.map((chatbot) => (
                                  <div key={chatbot.id} className="flex items-center justify-between p-2 bg-gray-600 rounded">
                                    <div className="flex items-center space-x-3">
                                      <Building className="w-4 h-4 text-violet-400" />
                                      <div>
                                        <span className="text-white text-sm">{chatbot.company_name}</span>
                                        <span className="text-gray-400 text-xs ml-2">({chatbot.company_website})</span>
                                      </div>
                                    </div>
                                    <Badge className={`text-xs ${
                                      chatbot.status === 'done' ? 'bg-green-600/20 text-green-300 border-green-500/30' :
                                      chatbot.status === 'deploy' ? 'bg-green-600/20 text-green-300 border-green-500/30' :
                                      chatbot.status === 'testing' ? 'bg-blue-600/20 text-blue-300 border-blue-500/30' :
                                      chatbot.status === 'in_progress' ? 'bg-yellow-600/20 text-yellow-300 border-yellow-500/30' :
                                      'bg-gray-600/20 text-gray-300 border-gray-500/30'
                                    }`}>
                                      {chatbot.status.replace('_', ' ')}
                                    </Badge>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })
                  )}
                </CardContent>
              </Card>
            </div>
          )}
        </div>

        {/* Create Employee Modal */}
        {showCreateForm && (
          <div className="fixed inset-0 z-60 bg-black/50 flex items-center justify-center p-4">
            <div className="bg-gray-800 border border-gray-600 rounded-lg w-full max-w-md">
              <div className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-bold text-white">Create New Employee</h3>
                  <Button
                    onClick={() => setShowCreateForm(false)}
                    variant="ghost"
                    size="sm"
                    className="text-gray-400 hover:text-white"
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>
                
                <form onSubmit={handleCreateEmployee} className="space-y-4">
                  <div>
                    <Label htmlFor="create-first-name" className="text-gray-300">First Name</Label>
                    <Input
                      id="create-first-name"
                      value={createForm.first_name}
                      onChange={(e) => setCreateForm(prev => ({ ...prev, first_name: e.target.value }))}
                      className="bg-gray-700 border-gray-600 text-white"
                      required
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="create-last-name" className="text-gray-300">Last Name</Label>
                    <Input
                      id="create-last-name"
                      value={createForm.last_name}
                      onChange={(e) => setCreateForm(prev => ({ ...prev, last_name: e.target.value }))}
                      className="bg-gray-700 border-gray-600 text-white"
                      required
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="create-email" className="text-gray-300">Email</Label>
                    <Input
                      id="create-email"
                      type="email"
                      value={createForm.email}
                      onChange={(e) => setCreateForm(prev => ({ ...prev, email: e.target.value }))}
                      className="bg-gray-700 border-gray-600 text-white"
                      required
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="create-password" className="text-gray-300">Password</Label>
                    <Input
                      id="create-password"
                      type="password"
                      value={createForm.password}
                      onChange={(e) => setCreateForm(prev => ({ ...prev, password: e.target.value }))}
                      className="bg-gray-700 border-gray-600 text-white"
                      required
                    />
                  </div>
                  
                  <div className="flex justify-end space-x-2 pt-4">
                    <Button
                      type="button"
                      onClick={() => setShowCreateForm(false)}
                      variant="outline"
                      className="border-gray-600 text-gray-400 hover:bg-gray-700"
                    >
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      disabled={createLoading}
                      className="bg-green-600 hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {createLoading ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Creating...
                        </>
                      ) : (
                        <>
                          <Save className="w-4 h-4 mr-2" />
                          Create Employee
                        </>
                      )}
                    </Button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}

        {/* Edit Employee Modal */}
        {showEditForm && selectedEmployee && (
          <div className="fixed inset-0 z-60 bg-black/50 flex items-center justify-center p-4">
            <div className="bg-gray-800 border border-gray-600 rounded-lg w-full max-w-md">
              <div className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-bold text-white">
                    Edit {selectedEmployee.first_name} {selectedEmployee.last_name}
                  </h3>
                  <Button
                    onClick={() => {
                      setShowEditForm(false);
                      setSelectedEmployee(null);
                    }}
                    variant="ghost"
                    size="sm"
                    className="text-gray-400 hover:text-white"
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>
                
                <form onSubmit={handleEditEmployee} className="space-y-4">
                  <div>
                    <Label htmlFor="edit-first-name" className="text-gray-300">First Name</Label>
                    <Input
                      id="edit-first-name"
                      value={editForm.first_name}
                      onChange={(e) => setEditForm(prev => ({ ...prev, first_name: e.target.value }))}
                      className="bg-gray-700 border-gray-600 text-white"
                      required
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="edit-last-name" className="text-gray-300">Last Name</Label>
                    <Input
                      id="edit-last-name"
                      value={editForm.last_name}
                      onChange={(e) => setEditForm(prev => ({ ...prev, last_name: e.target.value }))}
                      className="bg-gray-700 border-gray-600 text-white"
                      required
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="edit-email" className="text-gray-300">Email</Label>
                    <Input
                      id="edit-email"
                      type="email"
                      value={editForm.email}
                      onChange={(e) => setEditForm(prev => ({ ...prev, email: e.target.value }))}
                      className="bg-gray-700 border-gray-600 text-white"
                      required
                    />
                  </div>
                  
                  <div className="flex justify-end space-x-2 pt-4">
                    <Button
                      type="button"
                      onClick={() => {
                        setShowEditForm(false);
                        setSelectedEmployee(null);
                      }}
                      variant="outline"
                      className="border-gray-600 text-gray-400 hover:bg-gray-700"
                    >
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      disabled={editLoading}
                      className="bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {editLoading ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Updating...
                        </>
                      ) : (
                        <>
                          <Save className="w-4 h-4 mr-2" />
                          Update Employee
                        </>
                      )}
                    </Button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}

        {/* Fire Employee Modal */}
        {showFireDialog && selectedEmployee && (
          <div className="fixed inset-0 z-60 bg-black/50 flex items-center justify-center p-4">
            <div className="bg-gray-800 border border-red-600 rounded-lg w-full max-w-md">
              <div className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center space-x-3">
                    <AlertTriangle className="w-6 h-6 text-red-400" />
                    <h3 className="text-lg font-bold text-red-400">
                      Remove {selectedEmployee.first_name} {selectedEmployee.last_name}
                    </h3>
                  </div>
                  <Button
                    onClick={() => {
                      setShowFireDialog(false);
                      setSelectedEmployee(null);
                    }}
                    variant="ghost"
                    size="sm"
                    className="text-gray-400 hover:text-white"
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>
                
                <div className="mb-4 p-3 bg-red-950/20 border border-red-600/20 rounded-lg">
                  <p className="text-red-300 text-sm">
                    This action will permanently remove the employee from the system. 
                    All their assigned chatbots will be reassigned to another employee.
                  </p>
                </div>

                {(() => {
                  const employeeData = getEmployeeStats(selectedEmployee);
                  if (employeeData.chatbots.length > 0) {
                    return (
                      <div className="mb-4 p-3 bg-yellow-950/20 border border-yellow-600/20 rounded-lg">
                        <p className="text-yellow-300 text-sm font-medium mb-2">
                          This employee has {employeeData.chatbots.length} assigned chatbot(s):
                        </p>
                        <ul className="text-yellow-300 text-xs space-y-1">
                          {employeeData.chatbots.map((chatbot) => (
                            <li key={chatbot.id}>• {chatbot.company_name} ({chatbot.status})</li>
                          ))}
                        </ul>
                      </div>
                    );
                  }
                  return null;
                })()}
                
                <form onSubmit={handleFireEmployee} className="space-y-4">
                  {(() => {
                    const employeeData = getEmployeeStats(selectedEmployee);
                    if (employeeData.chatbots.length > 0) {
                      return (
                        <div>
                          <Label htmlFor="reassign-employee" className="text-gray-300">
                            Reassign chatbots to:
                          </Label>
                          <Select 
                            value={fireForm.reassignToEmployeeId}
                            onValueChange={(value) => setFireForm(prev => ({ ...prev, reassignToEmployeeId: value }))}
                          >
                            <SelectTrigger className="bg-gray-700 border-gray-600 text-white">
                              <SelectValue placeholder="Select employee..." />
                            </SelectTrigger>
                            <SelectContent className="bg-gray-700 border-gray-600">
                              {employees
                                .filter(emp => emp.id !== selectedEmployee.id)
                                .map((employee) => (
                                <SelectItem key={employee.id} value={employee.id} className="text-white hover:bg-gray-600">
                                  {employee.first_name} {employee.last_name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      );
                    }
                    return null;
                  })()}
                  
                  <div>
                    <Label htmlFor="fire-confirmation" className="text-gray-300">
                      Type "FIRE" to confirm removal
                    </Label>
                    <Input
                      id="fire-confirmation"
                      value={fireForm.confirmationText}
                      onChange={(e) => setFireForm(prev => ({ ...prev, confirmationText: e.target.value }))}
                      className="bg-gray-700 border-red-600 text-white"
                      placeholder="FIRE"
                      required
                    />
                  </div>
                  
                  <div className="flex justify-end space-x-2 pt-4">
                    <Button
                      type="button"
                      onClick={() => {
                        setShowFireDialog(false);
                        setSelectedEmployee(null);
                      }}
                      variant="outline"
                      className="border-gray-600 text-gray-400 hover:bg-gray-700"
                    >
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      disabled={fireLoading}
                      variant="destructive"
                      className="bg-red-600 hover:bg-red-700"
                    >
                      {fireLoading ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Removing...
                        </>
                      ) : (
                        <>
                          <Trash2 className="w-4 h-4 mr-2" />
                          Remove Employee
                        </>
                      )}
                    </Button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default EmployeeManagementModal;